import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Camera,
  Clock,
  CornerUpLeft,
  Edit3,
  FileCheck,
  FileText,
  History,
  Lock,
  MapPin,
  Paperclip,
  PlayCircle,
  User,
  X,
} from 'lucide-react';
import { apiGet, apiPatch, apiUpload, getFileBlob, openPdfPreview } from '../shared/apiClient';
import type { Chiamata, Squadra } from '../shared/types';
import { STATI_CHIAMATA } from '../shared/constants';
import { apriGoogleMaps } from '../shared/maps';
import { getTurnoDateStrRome } from '../shared/tz';
import { useToast } from '../hooks/useToast';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { FotoDisservizioModal } from '../components/FotoDisservizioModal';
import { ModalConfermaSquadraTurno } from '../components/ModalConfermaSquadraTurno';
import { SecureImage } from '../components/SecureImage';
import { ReportInline } from '../components/ReportInline';
export { ReportInline };

interface StoricoItem extends Chiamata {}

export function ChiamataDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: impostazioniSquadra } = useQuery<{ mostraDaTerminareSquadre: boolean; mostraAperteSquadre: boolean; permettiRifiutoChiamate: boolean }>({
    queryKey: ['squadra-impostazioni'],
    queryFn: async () => {
      const { data } = await apiGet<{ mostraDaTerminareSquadre: boolean; mostraAperteSquadre: boolean; permettiRifiutoChiamate: boolean }>('/squadre/impostazioni');
      return data;
    },
    staleTime: 30_000,
  });
  const permettiRifiuto = impostazioniSquadra?.permettiRifiutoChiamate ?? true;

  const detailQuery = useQuery({
    queryKey: ['chiamata', id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await apiGet<Chiamata>(`/chiamate/${id}`);
      return data;
    },
  });

  const { data: squadraMe } = useQuery<Squadra>({
    queryKey: ['squadra-me'],
    queryFn: async () => {
      const { data } = await apiGet<Squadra>('/squadre/me');
      return data;
    },
  });

  const [showConfermaSquadraModal, setShowConfermaSquadraModal] = useState(false);
  const turnoOggi = useMemo(() => getTurnoDateStrRome(), []);
  const dotazioneConfermata = squadraMe?.dotazioneTurno === turnoOggi;

  const handleVaiAlReport = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    if (!dotazioneConfermata) {
      setShowConfermaSquadraModal(true);
    } else if (id) {
      navigate(`/chiamata/${id}/report`);
    }
  };

  // Avvisi attivi per l'impianto della chiamata
  const avvisiQuery = useQuery({
    queryKey: ['avvisi-impianti-attivi'],
    queryFn: async () => {
      const { data } = await apiGet<any[]>('/avvisi-impianti', { soloAttivi: 'true' });
      return Array.isArray(data) ? data : [];
    },
    staleTime: 20000,
    enabled: !!detailQuery.data?.impianto?.id,
  });

  const avvisiImpianto = useMemo(() => {
    if (!avvisiQuery.data || !detailQuery.data?.impianto?.id) return [];
    const impId = detailQuery.data.impianto.id;
    return avvisiQuery.data.filter(
      (a: any) => !a.isGenerale && (a.impianti || []).some((imp: any) => imp.id === impId)
    );
  }, [avvisiQuery.data, detailQuery.data]);

  const storicoQuery = useQuery({
    queryKey: ['impianto-storico', detailQuery.data?.impianto?.codice, detailQuery.data?.id],
    enabled: !!detailQuery.data?.impianto?.codice,
    queryFn: async () => {
      const codice = detailQuery.data!.impianto.codice;
      const excludeId = detailQuery.data?.id;
      const { data } = await apiGet<StoricoItem[]>(`/impianti/${codice}/storico`, { limit: 10, excludeId });
      return data;
    },
  });

  const c = detailQuery.data;
  const storicoFiltrato = useMemo(() => {
    return (storicoQuery.data || []).filter((item) => item.id !== c?.id).slice(0, 10);
  }, [storicoQuery.data, c?.id]);
  const ultimiReport = useMemo(() => (c?.reports || []).filter((r) => r.redattoDa !== 'operatore'), [c]);
  const stato = c ? STATI_CHIAMATA[c.stato] : null;

  const fotoDisservizioRaw = c?.fotoDisservizio;
  const fotoDisservizioList = useMemo(() => {
    if (!fotoDisservizioRaw) return [];
    try {
      const arr = JSON.parse(fotoDisservizioRaw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }, [fotoDisservizioRaw]);

  const [preview, setPreview] = useState<{ url: string; tipo: string; nome?: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showRifiuta, setShowRifiuta] = useState(false);
  const [showFotoDisservizioModal, setShowFotoDisservizioModal] = useState(false);
  const [isUploadingFotoDisservizio, setIsUploadingFotoDisservizio] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!showRifiuta && !preview) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showRifiuta, preview]);

  const handleConfirmFotoDisservizio = async (files: File[]) => {
    if (!c) return;
    setIsUploadingFotoDisservizio(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append('foto', f));
      await apiUpload(`/chiamate/${c.id}/foto-disservizio`, form);
      toast({ type: 'success', message: 'Foto dello stato del disservizio salvate!' });
      queryClient.invalidateQueries({ queryKey: ['chiamata', id] });
      setShowFotoDisservizioModal(false);
    } catch (err: any) {
      toast({ type: 'error', message: err?.response?.data?.errore || 'Errore durante l\'invio delle foto' });
    } finally {
      setIsUploadingFotoDisservizio(false);
    }
  };

  const rifiutaMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiPatch<Chiamata>(`/chiamate/${id}/rifiuta`, {});
      return data;
    },
    onSuccess: (updated) => {
      if (id) {
        try { localStorage.removeItem(`roadhub_report_draft_${id}`); } catch { /* best-effort */ }
      }
      const isDaTerminare = updated.stato === 'sospesa';
      toast({
        type: 'success',
        message: isDaTerminare
          ? 'Chiamata rimessa tra quelle da terminare'
          : 'Chiamata spostata in chiamate aperte',
      });
      queryClient.invalidateQueries({ queryKey: ['pending-calls'] });
      queryClient.invalidateQueries({ queryKey: ['chiamate-aperte'] });
      queryClient.invalidateQueries({ queryKey: ['chiamate-da-terminare'] });
      queryClient.invalidateQueries({ queryKey: ['chiamata', id] });
      setShowRifiuta(false);
      navigate('/');
    },
    onError: (err: any) => {
      toast({ type: 'error', message: err?.response?.data?.errore || 'Rifiuto non riuscito' });
      setShowRifiuta(false);
    },
  });

  const accettaMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiPatch<Chiamata>(`/chiamate/${id}/accetta`, {});
      return data;
    },
    onSuccess: () => {
      toast({ type: 'success', message: '✅ Chiamata presa in carico (eventuale precedente rilasciata)' });
      queryClient.invalidateQueries({ queryKey: ['pending-calls'] });
      queryClient.invalidateQueries({ queryKey: ['chiamate-aperte'] });
      queryClient.invalidateQueries({ queryKey: ['chiamate-da-terminare'] });
      queryClient.invalidateQueries({ queryKey: ['chiamata', id] });
    },
    onError: (err: any) => {
      toast({ type: 'error', message: err?.response?.data?.errore || 'Presa in carico fallita' });
    },
  });

  const prendiMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiPatch<Chiamata>(`/chiamate/${id}/prendi`, {});
      return data;
    },
    onSuccess: () => {
      toast({ type: 'success', message: 'Chiamata presa in carico con successo' });
      queryClient.invalidateQueries({ queryKey: ['pending-calls'] });
      queryClient.invalidateQueries({ queryKey: ['chiamate-aperte'] });
      queryClient.invalidateQueries({ queryKey: ['chiamate-da-terminare'] });
      queryClient.invalidateQueries({ queryKey: ['chiamata', id] });
    },
    onError: (err: any) => {
      toast({ type: 'error', message: err?.response?.data?.errore || 'Presa in carico fallita' });
    },
  });

  const apriAllegato = async (allegatoId: string, nomeFile: string) => {
    if (!c?.impianto?.id) return;
    setPreviewLoading(true);
    try {
      // Per i PDF (e allegati non-immagine) usa lo stesso sistema dell'operatore:
      // blob + window.open nel viewer di sistema (funziona anche da mobile).
      // L'iframe inline con blob URL non viene renderizzato dai viewer PDF mobili.
      const path = `/impianti/${c.impianto.id}/allegati/${allegatoId}`;
      if (nomeFile.toLowerCase().endsWith('.pdf') || /\.pdf($|\?)/i.test(path)) {
        await openPdfPreview(path);
      } else {
        const { blob, url } = await getFileBlob(path);
        if (blob.type.startsWith('image/')) {
          setPreview({ url, tipo: blob.type || 'application/octet-stream', nome: nomeFile });
        } else {
          // Non immagine e non PDF: apri comunque con il viewer di sistema
          await openPdfPreview(path);
        }
      }
    } catch (err: any) {
      const msg =
        err?.response?.status === 403
          ? 'Permesso negato per questo allegato'
          : err?.response?.status === 404
          ? 'Allegato non trovato'
          : "Impossibile aprire l'allegato";
      toast({ type: 'error', message: msg });
    } finally {
      setPreviewLoading(false);
    }
  };

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-4">
        <div className="skeleton h-12 w-28" />
        <div className="skeleton h-64" />
        <div className="skeleton h-44" />
      </div>
    );
  }

  if (!c) {
    return (
      <div className="card-section text-center p-8">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
        <h3 className="font-bold text-slate-800 text-lg">Chiamata non trovata</h3>
        <button className="btn-secondary mt-4" onClick={() => navigate('/')}>
          Torna alla Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* ── BARRA SUPERIORE NAVIGAZIONE ── */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          className="btn-outline px-3.5 py-2 text-xs font-bold gap-1.5 shadow-2xs"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Indietro</span>
        </button>

        <span className="text-xs text-slate-500 font-semibold">
          ID #{c.id.slice(0, 8)}
        </span>
      </div>

      {/* ── CARD PRINCIPALE DETTAGLIO IMPIANTO ── */}
      <section className="ln-card p-4 sm:p-5 space-y-4">
        {/* Intestazione e stato */}
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`ln-pill ${
              c.stato === 'chiusa'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : c.stato === 'sospesa'
                ? 'bg-amber-50 text-amber-800 border-amber-200'
                : 'bg-sky-50 text-sky-700 border-sky-200'
            }`}>
              <span className={`ln-status-dot ${
                c.stato === 'chiusa' ? 'bg-emerald-500' : c.stato === 'sospesa' ? 'bg-amber-500' : 'bg-sky-500'
              }`} />
              {stato?.label || c.stato}
            </span>
            {c.inLavorazione && (
              <span className="inline-flex items-center gap-1.5 text-xs font-extrabold text-purple-700 bg-purple-50 border border-purple-200 px-2.5 py-0.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-purple-500" />
                In Lavorazione
              </span>
            )}
            {c.urgente && (
              <span className="inline-flex items-center gap-1.5 text-xs font-extrabold text-rose-800 bg-rose-50 border border-rose-300 px-2.5 py-0.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-rose-600" />
                URGENTE
              </span>
            )}
            {c.daCompletare && !c.urgente && (
              <span className="inline-flex items-center gap-1.5 text-xs font-extrabold text-amber-800 bg-amber-50 border border-amber-300 px-2.5 py-0.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Da completare
              </span>
            )}
            {c.daSegnalazione && !c.urgente && !c.daCompletare && (
              <span className="inline-flex items-center gap-1.5 text-xs font-extrabold text-emerald-800 bg-emerald-50 border border-emerald-300 px-2.5 py-0.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Da Segnalazione
              </span>
            )}
          </div>
          <span className="text-xs text-slate-400 font-medium flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {new Date(c.dataOra).toLocaleString('it-IT', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="ln-impianto-chip text-xs py-0.5 px-2.5 font-bold">
              [{c.impianto?.codice}]
            </span>
            {c.impianto?.tipo && (
              <span className="text-[11px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                {c.impianto.tipo}
              </span>
            )}
          </div>
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 leading-tight">
            {c.impianto?.nome}
          </h2>

          {/* Pulsante rapido Indicazioni / Google Maps */}
          {(c.impianto?.lat || c.impianto?.nome) && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => apriGoogleMaps(c.impianto)}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200/80 shadow-xs text-xs sm:text-sm font-bold active:scale-95 transition cursor-pointer"
                title="Apri le indicazioni stradali su Google Maps"
              >
                <MapPin className="w-4 h-4 text-sky-600 shrink-0" />
                <span>🗺️ Apri Google Maps</span>
              </button>
            </div>
          )}

          {/* Banner avvisi di centrale per questo impianto */}
          {avvisiImpianto.length > 0 && (
            <div className="mt-3 rounded-2xl border-2 border-amber-400 bg-amber-50 p-3.5 space-y-2">
              <div className="flex items-center gap-2 text-amber-900 font-extrabold text-xs uppercase tracking-wide">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>⚠️ Avvisi di Centrale ({avvisiImpianto.length})</span>
              </div>
              <div className="space-y-1.5">
                {avvisiImpianto.map((a: any) => (
                  <div key={a.id} className="bg-white border border-amber-200 rounded-xl px-3 py-2">
                    <p className="text-xs font-semibold text-slate-800 leading-snug">{a.testo}</p>
                    {a.isGenerale && (
                      <span className="mt-1 inline-block text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
                        📢 Comunicazione generale
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Descrizione segnalazione & guasto */}
        <div className="rounded-2xl bg-slate-50 border border-slate-200/80 p-3.5 space-y-2.5">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-slate-400" />
            Descrizione problema / guasto
          </div>

          {(c.segnalatoDa || c.guastoSegnalato) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-2.5 rounded-xl bg-white border border-slate-200/80 text-xs">
              <div>
                <span className="text-slate-400 font-bold uppercase text-[10px] block">Segnalato da:</span>
                <span className="font-extrabold text-slate-900">{c.segnalatoDa || 'Sala Operativa'}</span>
              </div>
              <div>
                <span className="text-slate-400 font-bold uppercase text-[10px] block">Guasto segnalato:</span>
                <span className="font-extrabold text-amber-900 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200 inline-block mt-0.5">
                  {c.guastoSegnalato || 'Non specificato'}
                </span>
              </div>
            </div>
          )}

          {c.descrizione && c.descrizione.trim() !== (c.guastoSegnalato || '').trim() && !c.descrizione.startsWith('Chiamata') && (
            <div className="space-y-0.5">
              {(c.segnalatoDa || c.guastoSegnalato) && (
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Note aggiuntive / Dettagli:
                </div>
              )}
              <p className="text-sm sm:text-base text-slate-800 font-medium leading-relaxed">
                {c.descrizione}
              </p>
            </div>
          )}
        </div>

        {/* Foto Stato Disservizio (all'arrivo sul posto) */}
        {fotoDisservizioList.length > 0 && (
          <div className="rounded-2xl bg-amber-50/60 border border-amber-200/80 p-3.5 space-y-2.5 animate-fade-in">
            <div className="text-xs font-bold text-amber-900 uppercase tracking-wider flex items-center gap-1.5">
              <span>📸 Foto Stato Disservizio (Arrivo sul posto - {fotoDisservizioList.length})</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {fotoDisservizioList.map((filename: string, idx: number) => (
                <div
                  key={idx}
                  className="relative group rounded-xl border border-amber-200 bg-white overflow-hidden aspect-square block shadow-2xs hover:shadow-md transition cursor-pointer"
                >
                  <SecureImage
                    src={`/api/uploads/${filename}`}
                    alt={`Foto disservizio ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onImageClick={(blobUrl) => {
                      window.open(blobUrl, '_blank', 'noopener,noreferrer');
                    }}
                  />
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[11px] font-bold pointer-events-none">
                    Ingrandisci 🔍
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Griglia dati accessori */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div className="rounded-xl bg-slate-50/80 border border-slate-100 p-3 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white text-slate-600 flex items-center justify-center border border-slate-200 shrink-0">
              <User className="w-4 h-4" />
            </div>
            <div>
              <div className="text-slate-400 font-semibold uppercase text-[10px]">Squadra assegnata</div>
              <div className="text-slate-900 font-bold text-sm">
                {c.squadra?.nome || 'In attesa di presa in carico'}
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-slate-50/80 border border-slate-100 p-3 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white text-slate-600 flex items-center justify-center border border-slate-200 shrink-0">
              <Calendar className="w-4 h-4" />
            </div>
            <div>
              <div className="text-slate-400 font-semibold uppercase text-[10px]">Creazione chiamata</div>
              <div className="text-slate-900 font-bold text-sm">
                {new Date(c.dataOra).toLocaleDateString('it-IT')}
              </div>
            </div>
          </div>
        </div>

        {/* Allegati impianto */}
        {c.impianto?.allegati && c.impianto.allegati.length > 0 && (
          <div className="pt-2 border-t border-slate-100 space-y-2">
            <div className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
              <Paperclip className="w-4 h-4 text-slate-400" />
              Documenti e schemi dell'impianto ({c.impianto.allegati.length})
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {c.impianto.allegati.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="rounded-xl border border-slate-200 bg-white hover:bg-slate-50 p-2.5 text-left transition flex items-center justify-between gap-2 group shadow-2xs"
                  onClick={() => apriAllegato(a.id, a.nomeFile)}
                  disabled={previewLoading}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span className="text-xs font-bold text-slate-800 truncate group-hover:text-emerald-700">
                      {a.nomeFile}
                    </span>
                  </div>
                  <span className="text-[10px] font-semibold text-slate-400 shrink-0">
                    {Math.max(1, Math.round(a.size / 1024))} KB
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── BOTTONI DI AZIONE ── */}
        <div className="pt-3 border-t border-slate-100 space-y-2.5">
          {c.stato === 'assegnata' ? (
            <>
              {!c.inLavorazione ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <button
                      type="button"
                      className="btn-primary w-full justify-center py-3.5 text-base gap-2 bg-emerald-600 hover:bg-emerald-700 shadow-md hover:shadow-lg transition font-extrabold cursor-pointer"
                      disabled={accettaMutation.isPending}
                      onClick={() => accettaMutation.mutate()}
                    >
                      <PlayCircle className="w-5 h-5" />
                      <span>{accettaMutation.isPending ? 'Presa in carico in corso…' : '▶️ Prendi in carico'}</span>
                    </button>
                    {permettiRifiuto && (
                      <button
                        type="button"
                        className="btn-outline w-full justify-center py-3.5 text-base text-amber-700 hover:bg-amber-50 border-amber-300 font-bold gap-2 cursor-pointer shadow-2xs transition"
                        onClick={() => setShowRifiuta(true)}
                      >
                        <CornerUpLeft className="w-5 h-5 text-amber-600" />
                        <span>Sposta in chiamate aperte</span>
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-center text-slate-500 font-medium">
                    Prendi in carico la chiamata per avviare l'intervento e compilare il report.
                  </p>
                  <button
                    type="button"
                    className="btn-outline w-full justify-center py-3 text-sm font-bold text-amber-800 bg-amber-50/80 hover:bg-amber-100 border-amber-300 gap-2 shadow-2xs transition"
                    onClick={() => setShowFotoDisservizioModal(true)}
                  >
                    <Camera className="w-4 h-4 text-amber-600" />
                    <span>Foto all'arrivo (scatta o carica)</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <button
                      type="button"
                      className="btn-primary w-full justify-center py-3.5 text-sm gap-2 bg-emerald-600 hover:bg-emerald-700 shadow-md hover:shadow-lg transition font-extrabold cursor-pointer"
                      onClick={handleVaiAlReport}
                    >
                      <PlayCircle className="w-5 h-5" />
                      <span>Compila Report Intervento</span>
                    </button>
                    {permettiRifiuto && (
                      <button
                        type="button"
                        className="btn-outline w-full justify-center py-3.5 text-base text-amber-700 hover:bg-amber-50 border-amber-300 font-bold gap-2 cursor-pointer shadow-2xs transition"
                        onClick={() => setShowRifiuta(true)}
                      >
                        <CornerUpLeft className="w-5 h-5 text-amber-600" />
                        <span>Sposta in chiamate aperte</span>
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-outline w-full justify-center py-3 text-sm font-bold text-amber-800 bg-amber-50/80 hover:bg-amber-100 border-amber-300 gap-2 shadow-2xs transition"
                    onClick={() => setShowFotoDisservizioModal(true)}
                  >
                    <Camera className="w-4 h-4 text-amber-600" />
                    <span>Foto all'arrivo (scatta o carica)</span>
                  </button>
                </>
              )}
            </>
          ) : c.stato === 'report_squadra' ? (
            <div className="space-y-2.5">
              <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs font-bold text-amber-800">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  <span>In attesa di revisione operatore</span>
                </div>
                <span className="text-[11px] text-amber-700 font-medium">Modificabile fino alla chiusura</span>
              </div>
              <button
                type="button"
                className="btn-primary w-full justify-center py-3.5 text-sm gap-2 bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition font-extrabold text-white cursor-pointer"
                onClick={handleVaiAlReport}
              >
                <Edit3 className="w-5 h-5" />
                <span>✏️ Modifica Report Inviato</span>
              </button>
            </div>
          ) : c.stato === 'sospesa' || c.stato === 'nuova' ? (
            <button
              className="btn-primary w-full justify-center py-3.5 text-base gap-2 shadow-md hover:shadow-lg transition"
              type="button"
              disabled={prendiMutation.isPending}
              onClick={() => prendiMutation.mutate()}
            >
              <PlayCircle className="w-5 h-5" />
              <span>{prendiMutation.isPending ? 'Assegnazione in corso…' : 'Prendi in carico'}</span>
            </button>
          ) : c.stato === 'chiusa' ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3.5 flex items-center justify-center gap-2 text-slate-600 font-semibold text-xs">
              <Lock className="w-4 h-4 text-slate-400" />
              <span>Chiamata chiusa definitivamente (Report non modificabile)</span>
            </div>
          ) : null}
        </div>
      </section>

      {/* ── SEZIONE REPORT ESISTENTI DI QUESTA CHIAMATA ── */}
      {ultimiReport.length > 0 && (
        <section className="card-section space-y-3">
          <div className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-emerald-600" />
            <h3 className="section-title mb-0">Report redatti per questa chiamata ({ultimiReport.length})</h3>
          </div>
          <div className="space-y-3">
            {ultimiReport.map((r) => (
              <ReportInline
                key={r.id}
                report={r}
                squadraNome={r.squadraNome || c.squadra?.nome}
                onPreview={(url, nome) => setPreview({ url, tipo: 'image/jpeg', nome })}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── SEZIONE STORICO INTERVENTI PRECEDENTI SULL'IMPIANTO (collassabile, chiusa di default) ── */}
      <CollapsibleSection
        icon={History}
        iconClass="bg-slate-100 text-slate-700"
        title="Storico ultimi 10 interventi su questo impianto"
        subtitle={
          storicoFiltrato.length > 0
            ? `${storicoFiltrato.length} interventi precedenti registrati`
            : 'Nessun intervento precedente'
        }
        defaultOpen={false}
      >
        <div className="mt-3 pt-3 border-t border-slate-100">
          {storicoQuery.isLoading ? (
              <div className="space-y-2.5">
                <div className="skeleton h-24" />
                <div className="skeleton h-24" />
              </div>
            ) : storicoFiltrato.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center">
                <p className="font-bold text-slate-700 text-sm">Nessuno storico precedente per questo impianto</p>
              </div>
            ) : (
              <div className="space-y-3">
                {storicoFiltrato.map((item) => {
                  let itemFotoDisservizio: string[] = [];
                  if (item.fotoDisservizio) {
                    try {
                      const parsed = typeof item.fotoDisservizio === 'string' ? JSON.parse(item.fotoDisservizio) : item.fotoDisservizio;
                      itemFotoDisservizio = Array.isArray(parsed) ? parsed : [];
                    } catch {
                      itemFotoDisservizio = [];
                    }
                  }

                  const itemReports = (item.reports || []).filter((r) => r.redattoDa !== 'operatore');

                  return (
                    <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-2xs space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs text-slate-400 font-semibold">
                            {new Date(item.dataOra).toLocaleString('it-IT')}
                          </div>
                          <div className="font-bold text-sm text-slate-900 mt-0.5">
                            {item.descrizione}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            Squadra: <span className="font-semibold text-slate-700">{item.squadra?.nome || '—'}</span>
                          </div>
                        </div>
                        <span className="badge-grigio text-[10px]">
                          {STATI_CHIAMATA[item.stato]?.label || item.stato}
                        </span>
                      </div>

                      {/* Foto stato disservizio all'arrivo dell'intervento precedente */}
                      {itemFotoDisservizio.length > 0 && (
                        <div className="rounded-xl bg-amber-50/60 border border-amber-200/80 p-2.5 space-y-1.5">
                          <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-900 uppercase tracking-wider">
                            <Camera className="w-3.5 h-3.5 text-amber-600" />
                            <span>Foto Arrivo / Disservizio ({itemFotoDisservizio.length})</span>
                          </div>
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 pt-0.5">
                            {itemFotoDisservizio.map((fn, idx) => (
                              <div
                                key={idx}
                                className="relative group rounded-xl border border-amber-200 bg-white overflow-hidden aspect-square block shadow-2xs hover:shadow-md transition cursor-pointer"
                                onClick={() => setPreview({ url: `/api/uploads/${fn}`, tipo: 'image/jpeg', nome: `Foto disservizio ${new Date(item.dataOra).toLocaleDateString('it-IT')} (${idx + 1}/${itemFotoDisservizio.length})` })}
                              >
                                <SecureImage
                                  src={`/api/uploads/${fn}`}
                                  alt={`Foto disservizio ${idx + 1}`}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                  onImageClick={(blobUrl) => {
                                    setPreview({ url: blobUrl, tipo: 'image/jpeg', nome: `Foto disservizio ${new Date(item.dataOra).toLocaleDateString('it-IT')} (${idx + 1}/${itemFotoDisservizio.length})` });
                                  }}
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold pointer-events-none">
                                  🔍 Zoom
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Report associati dell'intervento precedente */}
                      {itemReports.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-100 space-y-2">
                          <div className="text-xs font-bold text-slate-500">Report associati:</div>
                          {itemReports.map((rep) => (
                            <ReportInline
                              key={rep.id}
                              report={rep}
                              squadraNome={rep.squadraNome || item.squadra?.nome}
                              onPreview={(url, nome) => setPreview({ url, tipo: 'image/jpeg', nome })}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
      </CollapsibleSection>

      {/* ── MODALE DI CONFERMA SPOSTAMENTO IN CHIAMATE APERTE ── */}
      {permettiRifiuto && showRifiuta && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl space-y-4 animate-slide-up">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
              <CornerUpLeft className="w-6 h-6" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-extrabold text-slate-900">Sposta in chiamate aperte</h3>
              <p className="mt-2 text-xs sm:text-sm text-slate-600 leading-relaxed">
                Sei sicuro di voler spostare questa chiamata in chiamate aperte?{' '}
                {ultimiReport.length > 0
                  ? 'Poiché ha già dei report parziali, verrà rimessa tra le chiamate da terminare.'
                  : 'Verrà rimessa tra le chiamate aperte a disposizione di tutte le squadre.'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2.5 pt-2">
              <button
                className="btn-secondary w-full justify-center"
                type="button"
                onClick={() => setShowRifiuta(false)}
                disabled={rifiutaMutation.isPending}
              >
                Annulla
              </button>
              <button
                className="btn-warning w-full justify-center bg-amber-600 hover:bg-amber-700 text-white font-bold"
                type="button"
                onClick={() => rifiutaMutation.mutate()}
                disabled={rifiutaMutation.isPending}
              >
                {rifiutaMutation.isPending ? 'Spostamento…' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── MODALE FOTO STATO DISSERVIZIO ── */}
      <FotoDisservizioModal
        isOpen={showFotoDisservizioModal}
        chiamata={c}
        onConfirm={handleConfirmFotoDisservizio}
        onSkip={() => setShowFotoDisservizioModal(false)}
        isUploading={isUploadingFotoDisservizio}
      />

      {/* ── MODALE DI ANTEPRIMA ALLEGATO ── */}
      {preview && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[100] flex flex-col bg-slate-950/95 animate-fade-in">
          <div className="flex items-center justify-between p-3.5 bg-slate-900 border-b border-slate-800">
            <span className="text-white text-sm font-bold truncate pr-2">
              {preview.nome || 'Anteprima allegato'}
            </span>
            <button
              type="button"
              className="btn-ghost px-3 py-1.5 text-white hover:bg-white/10 rounded-xl text-xs"
              onClick={() => {
                if (preview?.url && preview.url.startsWith('blob:')) URL.revokeObjectURL(preview.url);
                setPreview(null);
              }}
            >
              <X className="w-4 h-4" /> Chiudi
            </button>
          </div>
          <div className="flex-1 overflow-auto p-2 flex items-center justify-center">
            {preview.tipo.startsWith('image/') ? (
              <img src={preview.url} alt="Allegato" className="max-w-full max-h-full object-contain rounded-lg" />
            ) : (
              <iframe src={preview.url} title="Allegato" className="w-full h-full border-0 rounded-lg bg-white" />
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Modale conferma o selezione squadra/collaboratori del turno */}
      <ModalConfermaSquadraTurno
        isOpen={showConfermaSquadraModal}
        onClose={() => setShowConfermaSquadraModal(false)}
        squadraMe={squadraMe}
        motivo="inizio_report"
        onConfermato={() => {
          setShowConfermaSquadraModal(false);
          if (id) navigate(`/chiamata/${id}/report`);
        }}
      />
    </div>
  );
}

